/**
 * Image URLs in the size the screen needs.
 *
 * Files in our storage (Lovable Cloud on Supabase) can be resized on the fly through the
 * "render/image" endpoint. Local assets, external URLs and video markers pass through unchanged.
 */

const STORAGE_OBJECT = /^(https:\/\/[a-z0-9-]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/;

export interface StorageImageOptions {
  /** 1–100, default 75 */
  quality?: number;
  /** default webp (keeps transparency) */
  format?: "webp" | "origin";
  height?: number;
  resize?: "cover" | "contain" | "fill";
}

export function isStorageImage(url: string | null | undefined): boolean {
  return !!url && STORAGE_OBJECT.test(url.split("?")[0] ?? "");
}

/** Same picture, resized by the storage to `width` pixels with its aspect ratio kept (webp, quality 75 by default). */
export function storageImage(url: string | null | undefined, width: number, options: StorageImageOptions = {}): string {
  if (!url) return "";
  const [base, query] = url.split("?");
  const m = STORAGE_OBJECT.exec(base ?? "");
  if (!m) return url;
  const params = new URLSearchParams(query ?? "");
  params.set("width", String(width));
  if (options.height) params.set("height", String(options.height));
  // Without an explicit mode the storage crops to width x original height (a 1312x875 photo
  // requested at width 800 came back as 800x875). "contain" keeps the aspect ratio.
  params.set("resize", options.resize ?? "contain");
  params.set("quality", String(options.quality ?? 75));
  params.set("format", options.format ?? "webp");
  return `${m[1]}/storage/v1/render/image/public/${m[2]}?${params.toString()}`;
}

/** srcset with several widths, or undefined for pictures the storage cannot resize. */
export function storageSrcSet(
  url: string | null | undefined,
  widths: number[] = [480, 800, 1200],
  options: StorageImageOptions = {},
): string | undefined {
  if (!isStorageImage(url)) return undefined;
  return widths.map((w) => `${storageImage(url, w, options)} ${w}w`).join(", ");
}
